import { Icon } from '@clarion/ClarionIcon';
import {
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuProps as AriaMenuProps,
  MenuSection as AriaMenuSection,
  MenuSectionProps as AriaMenuSectionProps,
  Collection,
  composeRenderProps,
  Header,
  MenuItemProps,
  Separator,
  SeparatorProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { cx } from '../util';
import { ClarionBasePopover, ClarionBasePopoverProps } from './ClarionBasePopover';
import { IconProps } from './ClarionIcon';

export const dropdownItemStyles = tv({
  base: 'group flex items-center gap-4 cursor-default select-none py-2 pl-3 pr-1 rounded-md outline-0! text-sm',
  variants: {
    isDisabled: {
      false: 'text-gray-900 dark:text-neutral-100',
      true: 'text-gray-300 dark:text-neutral-600',
    },
    isFocused: {
      true: 'bg-purple-600 dark:bg-purple-700 text-white forced-colors:text-[ButtonText] forced-colors:bg-[Highlight]',
    },
  },
  compoundVariants: [
    {
      isFocused: false,
      isOpen: true,
      className: 'bg-gray-100 dark:bg-neutral-700/60',
    },
  ],
});

interface MenuProps<T> extends AriaMenuProps<T> {
  placement?: ClarionBasePopoverProps['placement'];
}

export function ClarionMenu<T extends object>(props: MenuProps<T>) {
  return (
    <ClarionBasePopover placement={props.placement} className="min-w-[150px]">
      <AriaMenu
        {...props}
        className="p-1 outline-hidden! max-h-[inherit] overflow-auto [clip-path:inset(0_0_0_0_round_.4rem)]"
      />
    </ClarionBasePopover>
  );
}

export type ClarionMenuItemProps = MenuItemProps & {
  icon?: IconProps['icon'];
};

export function ClarionMenuItem(props: ClarionMenuItemProps) {
  const textValue =
    props.textValue || (typeof props.children === 'string' ? props.children : undefined);
  return (
    <AriaMenuItem textValue={textValue} {...props} className={dropdownItemStyles}>
      {composeRenderProps(props.children, (children, { selectionMode, isSelected, hasSubmenu }) => (
        <>
          {selectionMode !== 'none' && (
            <span className="flex items-center w-2">
              {isSelected && <Icon icon="small-tick" size={16} aria-hidden />}
            </span>
          )}
          {props.icon && <Icon icon={props.icon} size={16} aria-hidden />}
          <span
            className={cx(
              'flex items-center flex-1 gap-2 font-normal truncate group-selected:font-semibold pr-2',
              hasSubmenu && 'pr-6',
            )}
          >
            {children}
          </span>
          {hasSubmenu && (
            <Icon icon="chevron-right" size={16} aria-hidden className="absolute right-2" />
          )}
        </>
      ))}
    </AriaMenuItem>
  );
}

export function ClarionMenuSeparator(props: SeparatorProps) {
  return (
    <Separator {...props} className="mx-3 my-1 border-b border-gray-300 dark:border-neutral-700" />
  );
}

export interface MenuSectionProps<T> extends AriaMenuSectionProps<T> {
  title?: string;
  items?: any;
}

export function ClarionMenuSection<T extends object>(props: MenuSectionProps<T>) {
  return (
    <AriaMenuSection className="first:-mt-[5px] after:content-[''] after:block after:h-[5px]">
      <Header className="text-sm font-semibold text-gray-500 dark:text-neutral-300 px-4 py-1 truncate sticky -top-[5px] -mt-px -mx-1 z-10 bg-gray-100/60 dark:bg-neutral-700/30 border-y border-y-gray-200 dark:border-y-neutral-700 [&+*]:mt-1">
        {props.title}
      </Header>
      <Collection items={props.items}>{props.children}</Collection>
    </AriaMenuSection>
  );
}
